import { useAtomValue } from "jotai";
import {
  isInSessionWithSelectedClientAtom,
  selectedClientInfoAtom,
} from "../../state";
import { scheduledPatientInformationAtom } from "../../schedule-preview/state";
import { type PreVisitSummaryCardConnected } from "@grow-therapy-team/seedling-components";
import { ComponentProps, useMemo } from "react";
import { gql } from "@apollo/client";
import { Server } from "../../../apollo/types";
import { endOfToday, sub } from "date-fns";
import {
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables,
} from "./types";
import { useAppDependenciesContext } from "../../../state";
import { preVisitSummaryEnabledGate } from "../../../statsig/gates";
import { PreVisitSummaryCardNudgeWrapper } from "./PreVisitSummaryCardNudgeWrapper";
const TIME_START_LOWER_BOUND_MINUTES = 15;

type ViewModel = Partial<
  Pick<
    ComponentProps<typeof PreVisitSummaryCardConnected>,
    | "appointmentShortId"
    | "patientShortId"
    | "nudgeContext"
    | "preVisitSummaryCardComponent"
  >
>;

/**
 * Intended to query for the selected patient's upcoming appointment's short ID.
 */
export const PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY = gql`
  query PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatient(
    $filters: AppointmentsFilter!
  ) {
    allAppointments(filters: $filters) {
      edges {
        node {
          id
          appointmentShortId
        }
      }
    }
  }
`;

function getFifteenMinutesAgoISO() {
  return sub(new Date(), {
    minutes: TIME_START_LOWER_BOUND_MINUTES,
  }).toISOString();
}

export function usePreVisitSummaryCardWrapper(): ViewModel {
  const isInSessionWithSelectedClient = useAtomValue(
    isInSessionWithSelectedClientAtom,
  );
  const selectedPatientInfo = useAtomValue(selectedClientInfoAtom);
  const scheduledPatientInfo = useAtomValue(scheduledPatientInformationAtom);
  const selectedAppointmentShortId =
    selectedPatientInfo?.inSessionAppointmentShortId ||
    scheduledPatientInfo?.appointmentShortId;
  const patientShortId =
    selectedPatientInfo?.patientShortId ||
    scheduledPatientInfo?.patientShortId ||
    "";
  const { useFeatureGate, useQuery } = useAppDependenciesContext();
  const { value: isPreVisitSummaryEnabled } = useFeatureGate(
    preVisitSummaryEnabledGate,
  );

  const alreadyHasAppointmentShortId = !!selectedAppointmentShortId;

  const fifteenMinutesAgoISO = useMemo(getFifteenMinutesAgoISO, []);

  const { data } = useQuery<
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables
  >(
    PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY,
    {
      skip:
        !isPreVisitSummaryEnabled ||
        !!alreadyHasAppointmentShortId ||
        !patientShortId,
      variables: {
        filters: {
          patientShortIds: [patientShortId],
          timeStartRange: {
            begin: fifteenMinutesAgoISO,
            end: endOfToday().toISOString(),
          },
          canceled: false,
        },
      },
      context: { server: Server.MONOLITH },
    },
  );

  const upcomingAppointmentShortId =
    data?.allAppointments?.edges?.[0]?.node?.appointmentShortId;

  const appointmentShortId =
    selectedAppointmentShortId || upcomingAppointmentShortId;

  return {
    appointmentShortId,
    patientShortId,
    nudgeContext: isInSessionWithSelectedClient
      ? "telehealth-session"
      : "telehealth-waiting-room",
    preVisitSummaryCardComponent: PreVisitSummaryCardNudgeWrapper,
  };
}
